import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Axios from '~/axiosWrapper';

import Dimmer from '~/app/common/Dimmer';

import Text, { TextProps } from '../Text';

import Input from './Input';
import List, { ListProps } from './List';
import { ListDefaultItem } from './List/ListItem';

import classes from './style.module.scss';

type SearchPropsDefault<T> = TextProps & Pick<ListProps<T>, 'renderItem' | 'onSelect'>

export interface SearchProps<T> extends SearchPropsDefault<T> {
  searchUrl: string
  searchKey?: keyof T
  searchKeys?: Array<keyof T>
  renderValue?: (item: T) => string
  onOpen?: () => void
  onClose?: () => void
}

const Search = <T extends ListDefaultItem = ListDefaultItem>(
  {
    searchUrl,
    searchKey = 'name',
    searchKeys,
    renderItem,
    renderValue,
    defaultValue,
    onSelect,
    onOpen,
    onClose,
    ...props
  }: SearchProps<T>
) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [textValue, setTextValue] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [list, setList] = useState<T[]>([]);
  const [searchedList, setSearchedList] = useState<T[]>([]);

  const triggerTextValue = useCallback(item => {
    if (renderValue) {
      setTextValue(renderValue(item));
      return;
    }
    const rendered = renderItem ? renderItem(item) : item[searchKey];
    setTextValue(typeof rendered === 'string' ? rendered : String(item[searchKey] ?? ''));
  }, [renderItem, renderValue, searchKey]);

  /*const search = useCallback(
    (value: string, key = searchKey): Promise<any[]> => Axios.get<any[], any[]>(
      searchUrl,
      { params: { $filter: `contains(tolower(${key}),'${value.toLowerCase()}')` } }
    ),
    [searchKey, searchUrl]
  );*/

  const searchFormSearchHandler = useCallback((value: string): void => {
    setValue(value)
  }, []);

  const inputFocusHandler = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const closeHandler = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const listSelectHandler = useCallback((item: any): void => {
    setIsOpen(false);

    triggerTextValue(item);

    if (onSelect) {
      onSelect(item);
    }
  }, [onSelect, triggerTextValue]);

  const dimmerClickHandler = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (onOpen) {
        onOpen();
      }
    } else {
      // Reset the term only. `searchedList` is derived from `list` + `value` by the effect below,
      // so clearing it here would leave it empty on re-open when `value` was already ''.
      setValue('');

      if (onClose) {
        onClose();
      }
    }
  }, [isOpen, onClose, onOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const { style } = document.documentElement;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      setSearchedList(list.filter(item => (
        ((searchKeys?.length ? searchKeys.map(key => item[key]).join(" ") : renderItem ? renderItem(item) : item[searchKey]) as string).toLowerCase().indexOf(value.toLowerCase()) !== -1
      )));
    } else {
      // No search term: show all available options as soon as the field is opened.
      setSearchedList(list);
    }
  }, [list, renderItem, searchKeys, searchKey, value]);

  useEffect(() => {
    if (!defaultValue) {
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    Axios.get<T, T>(`${searchUrl}/${defaultValue}`)
      .then((item) => {
        if (!cancelled) {
          triggerTextValue(item);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Resolve initial label once when the field mounts (parent may remount via `key`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsSearching(true);

    Axios.get<T[], T[]>(searchUrl)
      .then((data) => {
        if (!cancelled) {
          setList(data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Load options once on mount; parent remounts when search context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Text {...props} ref={inputRef} icon="search" value={textValue} readOnly onFocus={inputFocusHandler} />

      {ReactDOM.createPortal((
        <Dimmer page show={isOpen} className={classes['search-dimmer']} onClick={dimmerClickHandler}>
          <div className={classes['search-wrap']}>
            <Input
              {...props}
              loading={isSearching}
              onSearch={searchFormSearchHandler}
              onFocus={inputFocusHandler}
              onClose={closeHandler}
              value={textValue}
            />

            {!isSearching && (
              <List<T> list={searchedList} searchKey={searchKey} searchKeys={searchKeys} searchTerm={value} renderItem={renderItem} onSelect={listSelectHandler} />
            )}
          </div>
        </Dimmer>
      ), (document.getElementById('root') as HTMLElement))}
    </>
  );
};

export default Search
